import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth, GOOGLE_CLIENT_ID, isMagicLink, getMagicLinkEmail, setAuthHintCookie } from '@/auth';
import { setSessionToken, clearSessionToken, emailFromSessionToken } from '@/auth/sessionToken';
import { Button, Input } from '@/components/ui';
import { apiService } from '@/api';
import { useUserStore } from '@/store/userStore';
import { trackLogin, trackLoginError, trackSignUpOnce, trackSignupStarted } from '@/services/analyticsService';
import { LoginVideoWall, LoginPosterBackdrop } from './LoginMedia';

// Declare Google Identity Services types
declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: {
            client_id: string;
            callback: (response: { credential: string }) => void;
            auto_select?: boolean;
            cancel_on_tap_outside?: boolean;
            context?: string;
            itp_support?: boolean;
          }) => void;
          prompt: (notification?: (notification: { isNotDisplayed: () => boolean; isSkippedMoment: () => boolean; getNotDisplayedReason: () => string; getSkippedReason: () => string }) => void) => void;
          cancel: () => void;
        };
      };
    };
  }
}

type ProviderMethod = 'google' | 'apple' | 'microsoft' | 'email' | 'onetap';

// AI model logos shown as a trust strip under the card (copied from the landing
// page into /public/login-assets/models).
const MODEL_LOGOS = ['google', 'openai', 'kling', 'bytedance', 'minimax', 'luma', 'alibaba', 'xai'];

// Sign-in errors that mean "the user changed their mind" — never surface these
// as an error message, it just looks broken.
const AUTH_CANCEL_CODES = new Set([
  'auth/popup-closed-by-user',
  'auth/cancelled-popup-request',
  'auth/user-cancelled',
]);

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// DEV-ONLY quick login (paste a user id or email → sign in). DOUBLE-GATED so it
// can NEVER reach production:
//   1. import.meta.env.DEV is a COMPILE-TIME constant — in `vite build` (prod) it
//      is statically false, so this whole helper + its UI are tree-shaken out of
//      the shipped bundle. It only exists in the local `vite dev` server.
//   2. Even inside a dev build, the hostname must be localhost / a private LAN
//      address (covers `npm run dev -- --host`). A dev build served on any public
//      host returns false.
function isLocalDevEnv(): boolean {
  if (!import.meta.env.DEV) return false;
  if (typeof window === 'undefined') return false;
  const h = window.location.hostname;
  return (
    h === 'localhost' || h === '127.0.0.1' || h === '0.0.0.0' || h === '::1' ||
    h.endsWith('.local') ||
    /^192\.168\./.test(h) || /^10\./.test(h) ||
    /^172\.(1[6-9]|2\d|3[01])\./.test(h)
  );
}

// Apple/Mac users see Apple sign-in promoted to the top of the secondary list.
function isApplePlatform(): boolean {
  if (typeof navigator === 'undefined') return false;
  return /iPhone|iPad|iPod|Macintosh/i.test(navigator.userAgent);
}

export function LoginPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const {
    signInWithGoogle,
    signInWithGoogleOneTap,
    signInWithApple,
    signInWithMicrosoft,
    completeMagicLinkSignIn,
    isAuthenticated: isFirebaseAuthenticated,
  } = useAuth();
  const { setUser, setUserId, userId, user } = useUserStore();
  const [error, setError] = useState<string | null>(null);
  const [isBypassLoading, setIsBypassLoading] = useState(false);
  // DEV-only quick-login field (uid or email). See isLocalDevEnv() above.
  // `import.meta.env.DEV` is FIRST so the bundler folds it to `false` in prod
  // (`false && …`) and tree-shakes the field, its handler and isLocalDevEnv()
  // entirely out of the shipped bundle — not just disables them at runtime.
  const devLoginEnabled = import.meta.env.DEV && isLocalDevEnv();
  const [devId, setDevId] = useState('');
  const [isOneTapLoading, setIsOneTapLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [isMicrosoftLoading, setIsMicrosoftLoading] = useState(false);
  const [isAppleLoading, setIsAppleLoading] = useState(false);
  const [isMagicLinkLoading, setIsMagicLinkLoading] = useState(false);

  // Email OTP (passwordless 6-digit code) login state
  const [showEmailInput, setShowEmailInput] = useState(false);
  const [emailInput, setEmailInput] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  // Sending the code stays INLINE (button spinner) — must NOT use isMagicLinkLoading,
  // which triggers the full-screen "signing in" takeover.
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);
  // Set when a magic link is opened on a device/browser that has no stored
  // email (cross-device) — we then ask the user to confirm their email.
  const [needsEmailConfirmation, setNeedsEmailConfirmation] = useState(false);
  const [disposableEmailDomains, setDisposableEmailDomains] = useState<Set<string> | null>(null);
  const [isValidatingEmail, setIsValidatingEmail] = useState(false);
  // Shine: 2× every 2.5s, then every 5.5s. Class is toggled off/on to restart CSS.
  const [shineRun, setShineRun] = useState(false);

  const isApple = isApplePlatform();
  // User is authenticated either via Firebase OR via bypass auth (uid in userStore)
  const isAuthenticated = isFirebaseAuthenticated || (!!userId && !!user);

  // Disable every provider button while any sign-in is in flight (prevents
  // double popups / double redirects).
  const anyLoading =
    isGoogleLoading ||
    isMicrosoftLoading ||
    isAppleLoading ||
    isValidatingEmail ||
    isMagicLinkLoading ||
    isSendingOtp ||
    isVerifyingOtp ||
    isBypassLoading ||
    isOneTapLoading;

  // Fetch disposable email domains blocklist
  const fetchDisposableEmailDomains = useCallback(async (): Promise<Set<string>> => {
    if (disposableEmailDomains) {
      return disposableEmailDomains;
    }

    try {
      const response = await fetch(
        'https://raw.githubusercontent.com/disposable-email-domains/disposable-email-domains/master/disposable_email_blocklist.conf'
      );
      const text = await response.text();
      const domains = new Set(
        text.split('\n')
          .map(line => line.trim().toLowerCase())
          .filter(line => line && !line.startsWith('#'))
      );
      setDisposableEmailDomains(domains);
      return domains;
    } catch (err) {
      console.error('Failed to fetch disposable email blocklist:', err);
      return new Set();
    }
  }, [disposableEmailDomains]);

  // Check if email is from a disposable domain. Never let a slow blocklist fetch
  // block the send — race it against a short timeout and allow sign-in through.
  const isDisposableEmail = async (email: string): Promise<boolean> => {
    const domain = email.split('@')[1]?.toLowerCase();
    if (!domain) return false;

    const blocklist = await Promise.race([
      fetchDisposableEmailDomains(),
      new Promise<Set<string>>((resolve) => setTimeout(() => resolve(new Set()), 1500)),
    ]);
    return blocklist.has(domain);
  };

  // Preserve BOTH path and query of the intended destination (e.g. an email CTA
  // like /subscription?from=email&tpl=… whose one-tap ticket had expired), not
  // just the pathname — otherwise the offer context is dropped after login.
  const fromLoc = (location.state as { from?: { pathname: string; search?: string } })?.from;
  const from = fromLoc?.pathname ? `${fromLoc.pathname}${fromLoc.search || ''}` : '/';

  // Shine cadence: settle → pulse, +2.5s → pulse again, then every 5.5s.
  useEffect(() => {
    if (typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return;
    }
    let cancelled = false;
    let slowInterval: number | undefined;
    const timeouts: number[] = [];

    const pulse = () => {
      if (cancelled) return;
      setShineRun(false);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (!cancelled) setShineRun(true);
        });
      });
    };

    const SETTLE_MS = 600;
    const FAST_GAP_MS = 2500;
    const SLOW_GAP_MS = 5500;

    timeouts.push(window.setTimeout(pulse, SETTLE_MS));
    timeouts.push(window.setTimeout(pulse, SETTLE_MS + FAST_GAP_MS));
    timeouts.push(window.setTimeout(() => {
      if (cancelled) return;
      pulse();
      slowInterval = window.setInterval(pulse, SLOW_GAP_MS);
    }, SETTLE_MS + FAST_GAP_MS + SLOW_GAP_MS));

    return () => {
      cancelled = true;
      timeouts.forEach((id) => window.clearTimeout(id));
      if (slowInterval !== undefined) window.clearInterval(slowInterval);
    };
  }, []);

  const handleSuccessfulSignIn = useCallback(() => {
    if (!localStorage.getItem('vivideo_has_visited')) {
      localStorage.setItem('vivideo_has_visited', 'true');
    }
    navigate(from, { replace: true });
  }, [navigate, from]);

  // Shared error handling: suppress user-cancelled dismissals, surface a
  // connection-specific message for network failures, and track everything else
  // as a login_error so the funnel is measurable.
  const handleAuthError = useCallback((err: unknown, method: ProviderMethod) => {
    const code = (err as { code?: string })?.code || 'unknown';
    console.error(`${method} sign-in failed:`, err);
    if (AUTH_CANCEL_CODES.has(code)) {
      return;
    }
    trackLoginError(method, code);
    setError(code === 'auth/network-request-failed' ? t('auth.networkError') : t('errors.somethingWentWrong'));
  }, [t]);

  // Handle Google One Tap credential callback
  const handleOneTapCallback = useCallback(async (response: { credential: string }) => {
    setIsOneTapLoading(true);
    setError(null);
    // One Tap is a Google auth intent — mirror the button flow's tracking.
    trackSignupStarted('google');
    try {
      await signInWithGoogleOneTap(response.credential);
      handleSuccessfulSignIn();
    } catch (err) {
      handleAuthError(err, 'onetap');
      setIsOneTapLoading(false);
    }
  }, [signInWithGoogleOneTap, handleSuccessfulSignIn, handleAuthError]);

  // Handle credential query parameter from landing page redirect
  useEffect(() => {
    const credential = searchParams.get('credential');
    if (credential && !isAuthenticated && !isOneTapLoading) {
      handleOneTapCallback({ credential });
    }
  }, [searchParams, isAuthenticated, isOneTapLoading, handleOneTapCallback]);

  // Support panel "Login as user": a staff-issued, backend-signed 1-hour session link.
  useEffect(() => {
    const token = searchParams.get('impersonate');
    if (token && !isAuthenticated) void handleImpersonation(token);
  }, [searchParams, isAuthenticated]);

  // Complete a magic-link sign-in with a known email.
  const completeMagicLink = useCallback(async (email: string) => {
    setIsMagicLinkLoading(true);
    setError(null);
    try {
      await completeMagicLinkSignIn(email);
      handleSuccessfulSignIn();
    } catch (err) {
      handleAuthError(err, 'email');
      // Let the user correct the email and try again.
      setNeedsEmailConfirmation(true);
      setIsMagicLinkLoading(false);
    }
  }, [completeMagicLinkSignIn, handleSuccessfulSignIn, handleAuthError]);

  // Handle magic link sign-in on page load
  useEffect(() => {
    if (!isMagicLink() || isAuthenticated || isMagicLinkLoading || needsEmailConfirmation) {
      return;
    }
    const storedEmail = getMagicLinkEmail();
    if (storedEmail) {
      completeMagicLink(storedEmail);
    } else {
      // Link opened on a different device/browser — ask for the email instead
      // of throwing a generic error.
      setNeedsEmailConfirmation(true);
    }
  }, [isAuthenticated, isMagicLinkLoading, needsEmailConfirmation, completeMagicLink]);

  // Initialize Google One Tap (popup prompt only — the sign-in button is always ours).
  useEffect(() => {
    if (isAuthenticated || searchParams.get('credential') || searchParams.get('impersonate')) {
      return;
    }

    const initGis = () => {
      if (!window.google?.accounts?.id) return;

      window.google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: handleOneTapCallback,
        auto_select: true,
        cancel_on_tap_outside: false,
        context: 'signin',
        itp_support: true,
      });

      window.google.accounts.id.prompt((notification) => {
        if (notification.isNotDisplayed()) {
          console.log('One Tap not displayed:', notification.getNotDisplayedReason());
        }
        if (notification.isSkippedMoment()) {
          console.log('One Tap skipped:', notification.getSkippedReason());
        }
      });
    };

    if (window.google?.accounts?.id) {
      initGis();
    } else {
      const checkGoogleLoaded = setInterval(() => {
        if (window.google?.accounts?.id) {
          clearInterval(checkGoogleLoaded);
          initGis();
        }
      }, 100);
      const timeoutId = setTimeout(() => clearInterval(checkGoogleLoaded), 5000);
      return () => {
        clearInterval(checkGoogleLoaded);
        clearTimeout(timeoutId);
      };
    }

    return () => {
      if (window.google?.accounts?.id) {
        window.google.accounts.id.cancel();
      }
    };
  }, [isAuthenticated, searchParams, handleOneTapCallback]);

  // Establishes the same session an email-OTP sign-in does, using the link's token.
  const handleImpersonation = async (token: string) => {
    setIsBypassLoading(true);
    setError(null);
    try {
      const email = emailFromSessionToken(token);
      if (!email) throw new Error('Unreadable login link');
      setSessionToken(token);
      const { userId: uid } = await apiService.getUserById(email);
      const signedInUser = await apiService.getUserInfo(uid);
      setUserId(uid);
      setUser(signedInUser);
      localStorage.setItem('vivideo_user_id', uid);
      localStorage.setItem('vivideo_email_auth', uid);
      trackLogin('bypass');
      navigate('/', { replace: true });
    } catch (err) {
      console.error('Login link failed:', err);
      clearSessionToken();
      setError(t('errors.sessionExpired'));
      setIsBypassLoading(false);
    }
  };

  // DEV-only: sign in from a pasted user id OR email. Resolves an email to its
  // uid first, then establishes the same user-store session the OTP/bypass flows
  // use (marked with vivideo_email_auth so it survives a refresh). Guarded by
  // isLocalDevEnv() at both compile time and runtime — never ships to prod.
  const handleDevLogin = async () => {
    if (!devLoginEnabled) return;
    const raw = devId.trim();
    if (!raw) return;
    setIsBypassLoading(true);
    setError(null);
    try {
      let uid = raw;
      if (raw.includes('@')) {
        const res = await apiService.getUserById(raw);
        uid = res.userId;
      }
      const signedInUser = await apiService.getUserInfo(uid);
      setUserId(uid);
      setUser(signedInUser);
      localStorage.setItem('vivideo_user_id', uid);
      localStorage.setItem('vivideo_email_auth', uid);
      setAuthHintCookie();
      trackLogin('bypass');
      if (!localStorage.getItem('vivideo_has_visited')) {
        localStorage.setItem('vivideo_has_visited', 'true');
      }
      navigate('/', { replace: true });
    } catch (err) {
      console.error('Dev login failed:', err);
      setError('Dev login failed — user id / email not found');
      setIsBypassLoading(false);
    }
  };

  // Redirect if already authenticated (via Firebase or bypass)
  if (isAuthenticated) {
    navigate(from, { replace: true });
    return null;
  }

  const handleGoogleSignIn = async () => {
    setError(null);
    setIsGoogleLoading(true);
    // Auth intent, BEFORE the provider round-trip — sign_up/login fire only
    // after auth succeeds, so this captures drop-off inside the OAuth step.
    trackSignupStarted('google');
    try {
      await signInWithGoogle();
      handleSuccessfulSignIn();
    } catch (err) {
      handleAuthError(err, 'google');
      setIsGoogleLoading(false);
    }
  };

  const handleMicrosoftSignIn = async () => {
    setError(null);
    setIsMicrosoftLoading(true);
    trackSignupStarted('microsoft');
    try {
      await signInWithMicrosoft();
      handleSuccessfulSignIn();
    } catch (err) {
      handleAuthError(err, 'microsoft');
      setIsMicrosoftLoading(false);
    }
  };

  const handleAppleSignIn = async () => {
    setError(null);
    setIsAppleLoading(true);
    trackSignupStarted('apple');
    try {
      await signInWithApple();
      handleSuccessfulSignIn();
    } catch (err) {
      handleAuthError(err, 'apple');
      setIsAppleLoading(false);
    }
  };

  const openEmailInput = () => {
    setShowEmailInput(true);
    // Prefetch the blocklist now so submit is instant later.
    void fetchDisposableEmailDomains();
  };

  // Send a 6-digit login code to the email (replaces the old magic link).
  const handleSendOtp = async () => {
    const email = emailInput.trim();

    if (!email || !EMAIL_REGEX.test(email)) {
      setError(t('common.enterYourEmail'));
      return;
    }

    setError(null);
    setIsValidatingEmail(true);

    try {
      const isDisposable = await isDisposableEmail(email);
      if (isDisposable) {
        setError(t('errors.disposableEmailNotAllowed'));
        setIsValidatingEmail(false);
        return;
      }
    } catch (err) {
      console.error('Email validation error:', err);
    }

    setIsValidatingEmail(false);
    setIsSendingOtp(true);
    trackSignupStarted('email');

    try {
      await apiService.sendEmailOtp(email);
      setOtpCode('');
      setOtpSent(true);
      setIsSendingOtp(false);
    } catch (err) {
      handleAuthError(err, 'email');
      setIsSendingOtp(false);
    }
  };

  // Verify the code, then establish the session the same way OAuth/bypass do:
  // get-or-create the user by email, load it, populate the store. Email-login
  // users are user-store-authenticated (no Firebase), like the bypass path.
  const handleVerifyOtp = async () => {
    const email = emailInput.trim();
    const code = otpCode.trim();
    if (code.length < 6) {
      setError(t('auth.enterCode'));
      return;
    }

    setError(null);
    setIsVerifyingOtp(true);
    try {
      const sessionToken = await apiService.verifyEmailOtp(email, code);
      if (!sessionToken) throw new Error('No session token from email-otp');
      setSessionToken(sessionToken);
      const { userId: uid, created } = await apiService.getUserById(email);
      const signedInUser = await apiService.getUserInfo(uid);
      setUserId(uid);
      setUser(signedInUser);
      localStorage.setItem('vivideo_user_id', uid);
      // Email-OTP login is NOT backed by a Firebase session, so mark it so
      // AuthContext restores (instead of clearing) the session on refresh.
      localStorage.setItem('vivideo_email_auth', uid);
      // No Firebase auth event fires for OTP logins — set the landing hint here.
      setAuthHintCookie();
      // A code sign-in can create the account (get-or-create 201): that is a sign-up.
      if (!(created && trackSignUpOnce(uid, 'email'))) trackLogin('email');
      handleSuccessfulSignIn();
    } catch (err) {
      console.error('OTP verify failed:', err);
      setError(t('auth.invalidCode'));
      setIsVerifyingOtp(false);
    }
  };

  const handleConfirmEmail = () => {
    const email = emailInput.trim();
    if (!EMAIL_REGEX.test(email)) {
      setError(t('common.enterYourEmail'));
      return;
    }
    setNeedsEmailConfirmation(false);
    completeMagicLink(email);
  };

  // Full-screen "signing in" state for the non-interactive completion flows.
  if (isBypassLoading || isOneTapLoading || (isMagicLinkLoading && !needsEmailConfirmation)) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-1/4 -left-1/4 w-1/2 h-1/2 bg-primary/20 rounded-full blur-3xl" />
          <div className="absolute bottom-1/4 -right-1/4 w-1/2 h-1/2 bg-accent/20 rounded-full blur-3xl" />
        </div>
        <div className="relative flex flex-col items-center gap-4">
          <img src="/logo.svg" alt={t('appName')} className="w-20 h-20 shadow-glow rounded-2xl animate-pulse" />
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
            <div className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
            <div className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
          </div>
          <p className="text-text-secondary">{t('auth.signingIn')}</p>
        </div>
      </div>
    );
  }

  const googleIcon = (
    <svg className="w-5 h-5" viewBox="0 0 24 24">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
    </svg>
  );

  const shineClass = shineRun ? 'login-btn-shine login-btn-shine--run' : 'login-btn-shine';

  const googleButton = (
    <div className={`w-full relative ${anyLoading && !isGoogleLoading && !isOneTapLoading ? 'pointer-events-none opacity-50' : ''}`}>
      <Button
        onClick={handleGoogleSignIn}
        isLoading={isGoogleLoading}
        disabled={anyLoading}
        variant="secondary"
        size="lg"
        className={`w-full justify-center gap-3 ${shineClass}`}
        style={{ ['--shine-delay' as string]: '0ms' }}
      >
        {!isGoogleLoading && googleIcon}
        {t('common.continueWithGoogle')}
      </Button>
    </div>
  );

  const microsoftButton = (
    <Button
      onClick={handleMicrosoftSignIn}
      isLoading={isMicrosoftLoading}
      disabled={anyLoading}
      variant="secondary"
      size="lg"
      className={`w-full justify-center gap-3 ${shineClass}`}
      style={{ ['--shine-delay' as string]: '140ms' }}
    >
      {!isMicrosoftLoading && (
        <svg className="w-5 h-5" viewBox="0 0 24 24">
          <path fill="#F25022" d="M1 1h10v10H1z"/>
          <path fill="#00A4EF" d="M1 13h10v10H1z"/>
          <path fill="#7FBA00" d="M13 1h10v10H13z"/>
          <path fill="#FFB900" d="M13 13h10v10H13z"/>
        </svg>
      )}
      {t('common.continueWithMicrosoft')}
    </Button>
  );

  const appleButton = (
    <Button
      onClick={handleAppleSignIn}
      isLoading={isAppleLoading}
      disabled={anyLoading}
      variant="secondary"
      size="lg"
      className={`w-full justify-center gap-3 ${shineClass}`}
      style={{ ['--shine-delay' as string]: '280ms' }}
    >
      {!isAppleLoading && (
        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
          <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
        </svg>
      )}
      {t('common.continueWithApple')}
    </Button>
  );

  const emailBlock = (
    <div className="space-y-3">
      {!showEmailInput ? (
        <Button
          onClick={openEmailInput}
          disabled={anyLoading}
          variant="secondary"
          size="lg"
          className={`w-full justify-center gap-3 ${shineClass}`}
          style={{ ['--shine-delay' as string]: '420ms' }}
        >
          <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="2" y="4" width="20" height="16" rx="2"/>
            <path d="M22 6l-10 7L2 6"/>
          </svg>
          {t('common.continueWithEmail')}
        </Button>
      ) : otpSent ? (
        <div className="space-y-3 p-4 bg-surface/50 border border-glass-border rounded-xl">
          <div className="text-center">
            <p className="text-sm font-medium text-text-primary">{t('auth.codeSentTitle')}</p>
            <p className="text-xs text-text-secondary mt-1">{t('auth.codeSentTo', { email: emailInput.trim() })}</p>
          </div>
          <Input
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            autoFocus
            maxLength={6}
            placeholder={t('auth.enterCode')}
            value={otpCode}
            onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            onKeyDown={(e) => e.key === 'Enter' && handleVerifyOtp()}
            className={`w-full text-center text-lg ${otpCode ? 'tracking-[0.5em]' : ''}`}
          />
          <Button
            onClick={handleVerifyOtp}
            isLoading={isVerifyingOtp}
            disabled={otpCode.length < 6}
            variant="primary"
            size="sm"
            className="w-full"
          >
            {t('auth.verifyCode')}
          </Button>
          <div className="flex items-center justify-center gap-4">
            <button
              type="button"
              onClick={handleSendOtp}
              className="text-xs font-medium text-primary hover:underline"
            >
              {t('auth.resendCode')}
            </button>
            <button
              type="button"
              onClick={() => {
                setOtpSent(false);
                setOtpCode('');
                setShowEmailInput(false);
                setEmailInput('');
                setError(null);
              }}
              className="text-xs font-medium text-text-secondary hover:underline"
            >
              {t('auth.useDifferentEmail')}
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-3 p-4 bg-surface/50 border border-glass-border rounded-xl">
          <Input
            type="email"
            autoFocus
            autoComplete="email"
            placeholder={t('common.enterYourEmail')}
            value={emailInput}
            onChange={(e) => setEmailInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSendOtp()}
            className="w-full"
          />
          <div className="flex gap-2">
            <Button
              onClick={() => {
                setShowEmailInput(false);
                setEmailInput('');
              }}
              variant="ghost"
              size="sm"
              className="flex-1"
            >
              {t('common.cancel')}
            </Button>
            <Button
              onClick={handleSendOtp}
              isLoading={isSendingOtp || isValidatingEmail}
              variant="primary"
              size="sm"
              className="flex-1"
            >
              {t('auth.sendCode')}
            </Button>
          </div>
        </div>
      )}
    </div>
  );

  const cardBody = needsEmailConfirmation ? (
    <div className="space-y-4">
      <div className="text-center">
        <h2 className="text-lg font-semibold text-text-primary">{t('auth.confirmEmailTitle')}</h2>
        <p className="text-sm text-text-secondary mt-1">{t('auth.confirmEmailToComplete')}</p>
      </div>
      <Input
        type="email"
        autoFocus
        autoComplete="email"
        placeholder={t('common.enterYourEmail')}
        value={emailInput}
        onChange={(e) => setEmailInput(e.target.value)}
        onKeyDown={(e) => e.key === 'Enter' && handleConfirmEmail()}
        className="w-full"
      />
      <Button
        onClick={handleConfirmEmail}
        isLoading={isMagicLinkLoading}
        variant="primary"
        size="lg"
        className="w-full justify-center"
      >
        {t('auth.completeSignIn')}
      </Button>
    </div>
  ) : (
    <div className="space-y-4">
      {googleButton}
      {isApple ? (
        <>
          {appleButton}
          {emailBlock}
          {microsoftButton}
        </>
      ) : (
        <>
          {microsoftButton}
          {emailBlock}
          {appleButton}
        </>
      )}
    </div>
  );

  return (
    <div className="min-h-screen w-full bg-background lg:grid lg:grid-cols-2">
      {/* Left: video wall (desktop only) */}
      <div className="relative hidden overflow-hidden bg-surface lg:block">
        <LoginVideoWall />
        {/* Readability overlay — wide edge fade for a softer handoff to login */}
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/40 to-transparent" />
        <div className="absolute inset-y-0 right-0 w-40 bg-gradient-to-l from-background via-background/60 to-transparent" />
        <div className="absolute inset-x-0 bottom-0 p-10">
          <h2 className="text-3xl font-bold text-text-primary">{t('auth.wallHeadline')}</h2>
          <p className="mt-2 max-w-md text-text-secondary">{t('auth.wallSubline')}</p>
        </div>
      </div>

      {/* Right: sign-in panel — nudged toward viewport center on desktop */}
      <div className="relative flex min-h-screen items-center justify-center p-6 lg:min-h-screen lg:px-12 xl:px-20">
        {/* Mobile-only blurred poster backdrop */}
        <div className="lg:hidden">
          <LoginPosterBackdrop />
        </div>
        {/* Gradient blobs */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-1/4 -left-1/4 w-1/2 h-1/2 bg-primary/20 rounded-full blur-3xl" />
          <div className="absolute bottom-1/4 -right-1/4 w-1/2 h-1/2 bg-accent/20 rounded-full blur-3xl" />
        </div>

        <div className="relative w-full max-w-md lg:-translate-x-10 xl:-translate-x-16">
          <div className="bg-surface/80 backdrop-blur-xl border border-glass-border rounded-3xl p-8 shadow-2xl">
            {/* Logo */}
            <div className="flex flex-col items-center mb-8">
              <img src="/logo.svg" alt={t('appName')} className="w-20 h-20 mb-4 shadow-glow rounded-2xl" />
              <h1 className="text-2xl font-bold text-text-primary">{t('appName')}</h1>
              <p className="text-text-secondary mt-2 text-center">
                {t('auth.loginSubtitle')}
              </p>
            </div>

            {/* Error Message */}
            {error && (
              <div className="mb-6 p-4 bg-status-error/10 border border-status-error/20 rounded-xl">
                <p className="text-sm text-status-error">{error}</p>
              </div>
            )}

            {cardBody}

            {/* DEV-ONLY quick login — compiled out of production builds. */}
            {devLoginEnabled && (
              <div className="mt-6 rounded-xl border border-dashed border-yellow-500/40 bg-yellow-500/5 p-4">
                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-yellow-500/90">
                  Dev login (local only)
                </p>
                <div className="flex gap-2">
                  <Input
                    type="text"
                    autoComplete="off"
                    placeholder="user id or email"
                    value={devId}
                    onChange={(e) => setDevId(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleDevLogin()}
                    className="flex-1"
                  />
                  <Button
                    onClick={handleDevLogin}
                    disabled={!devId.trim()}
                    variant="primary"
                    size="sm"
                  >
                    Log in
                  </Button>
                </div>
              </div>
            )}

            {/* Terms */}
            <p className="mt-8 text-xs text-text-tertiary text-center">
              {t('auth.termsAgreement')}{' '}
              <a
                href="https://vivideo.ai/terms-of-service"
                target="_blank"
                rel="noopener noreferrer"
                className="underline hover:text-text-secondary"
              >
                {t('about.termsOfService')}
              </a>
              {' & '}
              <a
                href="https://vivideo.ai/privacy-policy"
                target="_blank"
                rel="noopener noreferrer"
                className="underline hover:text-text-secondary"
              >
                {t('about.privacyPolicy')}
              </a>
            </p>
          </div>

          {/* Trust row: AI model logos */}
          <div className="mt-8">
            <p className="text-center text-xs text-text-tertiary mb-3">{t('auth.poweredBy')}</p>
            <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-3">
              {MODEL_LOGOS.map((name) => (
                <img
                  key={name}
                  src={`/login-assets/models/${name}.webp`}
                  alt=""
                  aria-hidden="true"
                  loading="lazy"
                  decoding="async"
                  className="h-5 w-auto object-contain opacity-50 grayscale transition-opacity hover:opacity-80"
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
